"use client";

import Link from "next/link";
import {
  AtSign,
  Bell,
  Building2,
  CalendarDays,
  ClipboardList,
  FileText,
  Handshake,
  Heart,
  Info,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  RefreshCw,
  Reply,
  Users,
} from "lucide-react";

import { cn, formatRelativeTime } from "@/lib/utils";
import {
  categoryForNotificationType,
  type AppNotification,
  type NotificationType,
} from "@/lib/notifications/types";

const TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  comment_on_post: MessageSquare,
  comment_reply: Reply,
  reaction_on_post: Heart,
  mention: AtSign,
  community_invite: Users,
  community_join_request: Users,
  community_join_approved: Users,
  event_registration_confirmed: CalendarDays,
  event_updated: CalendarDays,
  event_cancelled: CalendarDays,
  event_reminder: CalendarDays,
  chat_direct_message: MessageCircle,
  chat_group_message: MessagesSquare,
  service_request_created: ClipboardList,
  service_request_status_changed: RefreshCw,
  service_offer_received: Handshake,
  service_document_received: FileText,
  practice_invite: Building2,
  practice_join_request: Building2,
  practice_join_approved: Building2,
  practice_join_declined: Building2,
  practice_invite_accepted: Building2,
  practice_invite_declined: Building2,
  system_announcement: Megaphone,
  system_message: Info,
};

type NotificationItemProps = {
  notification: AppNotification;
  compact?: boolean;
  onOpen: (notification: AppNotification) => void;
};

export function NotificationItem({
  notification,
  compact = false,
  onOpen,
}: NotificationItemProps) {
  const Icon = TYPE_ICONS[notification.type];
  const category = categoryForNotificationType(notification.type);

  return (
    <Link
      href={notification.targetPath}
      onClick={(event) => {
        event.preventDefault();
        onOpen(notification);
      }}
      className={cn(
        "flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/70",
        !notification.isRead && "bg-primary/5",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full",
          notification.isRead
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 text-primary",
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-3">
          <span className="text-sm font-medium leading-snug">
            {notification.title}
          </span>
          {!notification.isRead ? (
            <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
          ) : null}
        </span>
        {notification.body ? (
          <span
            className={cn(
              "mt-0.5 block text-sm text-muted-foreground",
              compact && "line-clamp-2",
            )}
          >
            {notification.body}
          </span>
        ) : null}
        <span className="mt-1 block text-xs text-muted-foreground">
          {formatRelativeTime(notification.createdAt)}
          {compact ? null : ` · ${labelForCategory(category)}`}
        </span>
      </span>
    </Link>
  );
}

function labelForCategory(category: ReturnType<typeof categoryForNotificationType>) {
  switch (category) {
    case "community":
      return "Közösség";
    case "chat":
      return "Chat";
    case "service":
      return "Szolgáltatás";
    case "system":
      return "Rendszer";
  }
}
